import { PARTS, SHOP_PARTS, STARTER_PART_IDS } from "./parts";
import { STORAGE_KEY } from "./constants";
import { migratePartId, migrateUnlockedPartIds } from "./partIdMigration";
import { normalizePlacements } from "./placements";
import { starsForDifficulty } from "./stars";
import type { DifficultyLevel, PlacedPart, PlayerProgress, PlayerStats } from "./types";

const LEGACY_STORAGE_KEY = "rocket-builder-progress-v1";

export function defaultStats(): PlayerStats {
  return {
    activitySessions: {},
    activityWins: {},
    rocketsLaunched: 0,
    builderVisits: 0,
    activitiesMenuVisits: 0,
  };
}

export function defaultProgress(): PlayerProgress {
  return {
    unlockedPartIds: [...STARTER_PART_IDS],
    starBalance: 0,
    completedActivityDifficulties: {},
    builtRocket: [],
    claimedFlyRewards: [],
    difficulty: "easy",
    stats: defaultStats(),
  };
}

function touchPlayedAt(stats: PlayerStats): PlayerStats {
  const now = new Date().toISOString();
  return {
    ...stats,
    firstPlayedAt: stats.firstPlayedAt ?? now,
    lastPlayedAt: now,
  };
}

function bumpRecord(record: Record<string, number>, key: string): Record<string, number> {
  return { ...record, [key]: (record[key] ?? 0) + 1 };
}

function readRawProgress(): Record<string, unknown> | null {
  if (typeof window === "undefined") return null;
  try {
    const raw =
      localStorage.getItem(STORAGE_KEY) ?? localStorage.getItem(LEGACY_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as Record<string, unknown>;
  } catch {
    return null;
  }
}

function migrateLegacyProgress(parsed: Record<string, unknown>): PlayerProgress {
  const base = defaultProgress();
  const legacyIds = parsed.completedActivityIds as string[] | undefined;
  const completedActivityDifficulties: Record<string, DifficultyLevel[]> = {
    ...(parsed.completedActivityDifficulties as Record<string, DifficultyLevel[]> | undefined),
  };

  if (legacyIds?.length) {
    for (const activityId of legacyIds) {
      if (!completedActivityDifficulties[activityId]?.length) {
        completedActivityDifficulties[activityId] = ["easy", "medium", "hard"];
      }
    }
  }

  const unlockedPartIds = migrateUnlockedPartIds([
    ...STARTER_PART_IDS,
    ...((parsed.unlockedPartIds as string[] | undefined) ?? []),
  ]);

  return {
    ...base,
    ...parsed,
    unlockedPartIds,
    starBalance:
      typeof parsed.starBalance === "number" && Number.isFinite(parsed.starBalance)
        ? Math.max(0, Math.floor(parsed.starBalance))
        : 0,
    completedActivityDifficulties,
    builtRocket: normalizePlacements(parsed.builtRocket),
    claimedFlyRewards: Array.isArray(parsed.claimedFlyRewards)
      ? (parsed.claimedFlyRewards as string[])
      : [],
    difficulty: (parsed.difficulty as DifficultyLevel | undefined) ?? "easy",
    stats: {
      ...defaultStats(),
      ...(parsed.stats as PlayerStats | undefined),
    },
  };
}

export function loadProgress(): PlayerProgress {
  const parsed = readRawProgress();
  if (!parsed) return defaultProgress();
  return migrateLegacyProgress(parsed);
}

export function saveProgress(progress: PlayerProgress): void {
  if (typeof window === "undefined") return;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(progress));
}

export function isActivityDifficultyComplete(
  progress: PlayerProgress,
  activityId: string,
  difficulty: DifficultyLevel,
): boolean {
  return progress.completedActivityDifficulties[activityId]?.includes(difficulty) ?? false;
}

export function completedDifficultyCount(
  progress: PlayerProgress,
  activityId: string,
): number {
  return progress.completedActivityDifficulties[activityId]?.length ?? 0;
}

export function isPartUnlocked(progress: PlayerProgress, partId: string): boolean {
  return progress.unlockedPartIds.includes(partId);
}

export function canAffordPart(progress: PlayerProgress, partId: string): boolean {
  const part = PARTS.find((p) => p.id === partId);
  if (!part?.starCost) return false;
  if (isPartUnlocked(progress, partId)) return false;
  return progress.starBalance >= part.starCost;
}

export function completeActivityDifficulty(
  progress: PlayerProgress,
  activityId: string,
  difficulty: DifficultyLevel,
): PlayerProgress {
  const starsEarned = starsForDifficulty(difficulty);
  const already = isActivityDifficultyComplete(progress, activityId, difficulty);
  const completed = progress.completedActivityDifficulties[activityId] ?? [];

  const withStars: PlayerProgress = {
    ...progress,
    starBalance: progress.starBalance + starsEarned,
    completedActivityDifficulties: already
      ? progress.completedActivityDifficulties
      : {
          ...progress.completedActivityDifficulties,
          [activityId]: [...completed, difficulty],
        },
  };

  return recordActivityWin(withStars, activityId);
}

export function purchasePart(progress: PlayerProgress, partId: string): PlayerProgress | null {
  const part = PARTS.find((p) => p.id === partId);
  if (!part?.starCost || isPartUnlocked(progress, partId)) return null;
  if (progress.starBalance < part.starCost) return null;

  return {
    ...progress,
    starBalance: progress.starBalance - part.starCost,
    unlockedPartIds: [...progress.unlockedPartIds, partId],
  };
}

export function setDifficulty(
  progress: PlayerProgress,
  difficulty: PlayerProgress["difficulty"],
): PlayerProgress {
  return { ...progress, difficulty };
}

export function saveBuiltRocket(
  progress: PlayerProgress,
  builtRocket: PlacedPart[],
): PlayerProgress {
  return { ...progress, builtRocket: [...builtRocket] };
}

export function claimFlyReward(
  progress: PlayerProgress,
  rewardId: string,
  partId: string,
): PlayerProgress | null {
  if (progress.claimedFlyRewards.includes(rewardId)) return null;
  const resolvedPartId = migratePartId(partId);
  if (!PARTS.find((p) => p.id === resolvedPartId)) return null;

  const unlocked = progress.unlockedPartIds.includes(resolvedPartId)
    ? progress.unlockedPartIds
    : [...progress.unlockedPartIds, resolvedPartId];

  return {
    ...progress,
    unlockedPartIds: unlocked,
    claimedFlyRewards: [...progress.claimedFlyRewards, rewardId],
  };
}

export function recordBuilderVisit(progress: PlayerProgress): PlayerProgress {
  return {
    ...progress,
    stats: {
      ...touchPlayedAt(progress.stats),
      builderVisits: progress.stats.builderVisits + 1,
    },
  };
}

export function recordActivitiesMenuVisit(progress: PlayerProgress): PlayerProgress {
  return {
    ...progress,
    stats: {
      ...touchPlayedAt(progress.stats),
      activitiesMenuVisits: progress.stats.activitiesMenuVisits + 1,
    },
  };
}

export function recordActivitySession(
  progress: PlayerProgress,
  activityId: string,
): PlayerProgress {
  return {
    ...progress,
    stats: {
      ...touchPlayedAt(progress.stats),
      activitySessions: bumpRecord(progress.stats.activitySessions, activityId),
    },
  };
}

export function recordActivityWin(
  progress: PlayerProgress,
  activityId: string,
): PlayerProgress {
  return {
    ...progress,
    stats: {
      ...touchPlayedAt(progress.stats),
      activityWins: bumpRecord(progress.stats.activityWins, activityId),
    },
  };
}

export function recordRocketLaunch(progress: PlayerProgress): PlayerProgress {
  return {
    ...progress,
    stats: {
      ...touchPlayedAt(progress.stats),
      rocketsLaunched: progress.stats.rocketsLaunched + 1,
    },
  };
}

export function addStars(progress: PlayerProgress, amount: number): PlayerProgress {
  const stars = Math.max(0, Math.floor(amount));
  if (stars <= 0) return progress;
  return {
    ...progress,
    starBalance: progress.starBalance + stars,
  };
}

export function shopPartsRemaining(progress: PlayerProgress): number {
  return SHOP_PARTS.filter((p) => !isPartUnlocked(progress, p.id)).length;
}
